import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Clock, X, CreditCard } from 'lucide-react';
import { Button } from './ui/button';
import { useAuth } from '../contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const TRIAL_END_BANNER_DISMISS_KEY = 'trialEndBanner_dismissedAt';
const DISMISS_TTL_MS = 12 * 60 * 60 * 1000; // 12h — re-show after half a day

/**
 * Sticky banner that appears in the last 3 days of a trial OR when the
 * trial has already expired but the user is still being routed back to
 * /trial-expired. Routes to /billing/subscribe so the customer adds a
 * payment method and provisions a paid subscription before the trial
 * ends. Auto-hides once `has_active_subscription=true`.
 */
export default function TrialEndBanner() {
  const navigate = useNavigate();
  const { workspaceId } = useParams();
  const { authToken, isAuthenticated, user } = useAuth();
  const [state, setState] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  // Resolve the workspace prefix used by the rest of the tenant routes.
  // Super Admin (no workspaceId) should not see this banner — they
  // operate at platform level and don't have a customer-facing trial.
  const resolvedWorkspace = workspaceId || user?.workspaceId;

  useEffect(() => {
    if (!isAuthenticated || !authToken) return;
    fetch(`${API_URL}/api/billing/subscription`, {
      headers: { Authorization: `Bearer ${authToken}` },
    })
      .then((r) => (r.ok ? r.json() : null))
      .then(setState)
      .catch(() => {});
  }, [isAuthenticated, authToken]);

  // Honour a recent dismissal so we don't pester the user every page nav.
  useEffect(() => {
    const ts = parseInt(localStorage.getItem(TRIAL_END_BANNER_DISMISS_KEY) || '0', 10);
    if (ts && Date.now() - ts < DISMISS_TTL_MS) setDismissed(true);
  }, []);

  if (!state || dismissed) return null;
  if (!resolvedWorkspace) return null;  // Super Admin / pre-workspace contexts
  if (!state.is_trial || state.has_active_subscription) return null;
  if (!state.trial_expires_at) return null;

  const expiresAt = new Date(state.trial_expires_at);
  const now = new Date();
  const msLeft = expiresAt.getTime() - now.getTime();
  const daysLeft = Math.ceil(msLeft / (1000 * 60 * 60 * 24));

  // Only show in the final 3 days OR when already expired
  if (daysLeft > 3) return null;

  const expired = msLeft <= 0;
  const urgent = daysLeft <= 1 || expired;

  const handleDismiss = () => {
    localStorage.setItem(TRIAL_END_BANNER_DISMISS_KEY, String(Date.now()));
    setDismissed(true);
  };

  return (
    <div
      className={`relative w-full border-b ${
        urgent
          ? 'bg-rose-50 border-rose-200 text-rose-900'
          : 'bg-amber-50 border-amber-200 text-amber-900'
      }`}
      data-testid="trial-end-banner"
    >
      <div className="max-w-7xl mx-auto px-4 py-2.5 flex items-center justify-between gap-4">
        <div className="flex items-center gap-2 min-w-0">
          <Clock className={`w-4 h-4 flex-shrink-0 ${urgent ? 'text-rose-600' : 'text-amber-600'}`} />
          <p className="text-sm truncate">
            {expired ? (
              <>Your trial has expired. <strong>Add a payment method to keep your workspace active.</strong></>
            ) : daysLeft === 1 ? (
              <>Your trial ends <strong>tomorrow</strong>. Add payment now to avoid interruption.</>
            ) : (
              <>Your trial ends in <strong>{daysLeft} days</strong>. Add payment to continue without interruption.</>
            )}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <Button
            size="sm"
            onClick={() => navigate(`/${resolvedWorkspace}/billing/subscribe`)}
            className={
              urgent
                ? 'bg-rose-600 hover:bg-rose-700 text-white h-8'
                : 'bg-amber-600 hover:bg-amber-700 text-white h-8'
            }
            data-testid="trial-banner-add-payment-btn"
          >
            <CreditCard className="w-3.5 h-3.5 mr-1.5" />
            Add Payment
          </Button>
          {!expired && (
            <button
              onClick={handleDismiss}
              className="p-1 rounded hover:bg-black/5 transition-colors"
              aria-label="Dismiss"
              data-testid="trial-banner-dismiss-btn"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
